// ─── cTrader Order Alert Watcher ──────────────────────────────
// Tracks live cTrader positions and sends a single Telegram alert per
// order change (open / close / modify), avoiding spam.
//
// How it works:
//   - Subscribes to the shared SSE stream per account (empty symbol list —
//     the stream's server-driven reconcile bootstraps symbols from open
//     positions, same as the copy-trade engine does).
//   - On every `positions` snapshot it diffs the current position map
//     against the previous one:
//         new positionId          → OPEN
//         removed positionId      → CLOSE
//         changed sl/tp/volume    → MODIFY
//   - Anti-spam: the FIRST snapshot per account is "primed" (recorded
//     without firing) so startup backlog is never re-alerted. Subsequent
//     diffs only fire on a genuine change. A dedup key (stable per event)
//     collapses cross-tab double-sends via the edge function's dedup table.
//
// Runs independently of the Copy Trade engine — as long as this watcher is
// started (see useCtraderOrderAlert) the account is monitored.

import { subscribeSharedStream, type StreamEvent } from '@/lib/shared-ctrader-stream';
import { parseSymbolRes } from '@/lib/ctrader-parsers';
import { sendTelegram, TELEGRAM_CATEGORIES } from '@/lib/telegram-sender';

// ─── Types ──────────────────────────────────────────────────

export type OrderEventType = 'OPEN' | 'CLOSE' | 'MODIFY';

interface OrderState {
  symbolName: string;
  side: 'BUY' | 'SELL';
  volume: number; // lots
  sl: number | null;
  tp: number | null;
}

export interface OrderAlertAccount {
  accountId: number;
  accountName: string;
}

// ─── Module state ───────────────────────────────────────────

/** accountKey (string) → Map<positionId, OrderState> */
const orderStatesByAccount = new Map<string, Map<number, OrderState>>();
/** accountKey → unsubscribe fn from the shared stream */
const unsubscribers = new Map<string, () => void>();
/** accountKey → display label (name fallback id) */
const accountLabels = new Map<string, string>();
/** accounts whose first snapshot has been primed (baseline recorded) */
const primedByAccount = new Set<string>();

// ─── Helpers ────────────────────────────────────────────────

function accountKey(accountId: number): string {
  return String(accountId);
}

/** Parse the raw reconcile payload into a Map<positionId, OrderState>. */
function parseOrderStates(
  reconcileRes: Record<string, unknown>,
  symbolRes: Record<string, unknown> | null,
): Map<number, OrderState> {
  const symbolMap = parseSymbolRes(symbolRes || {});
  const posArray = Array.isArray(reconcileRes.position)
    ? (reconcileRes.position as Array<Record<string, unknown>>)
    : [];
  const map = new Map<number, OrderState>();

  for (const p of posArray) {
    if (typeof p !== 'object' || p === null) continue;
    const td = (p.tradeData as Record<string, unknown>) || {};
    const pid = typeof p.positionId === 'number' ? p.positionId : 0;
    if (pid <= 0) continue;

    const volRaw = typeof td.volume === 'number' ? td.volume : 0;
    const volLots = volRaw / 10_000_000;
    const sideRaw = typeof td.tradeSide === 'number' ? td.tradeSide : 1;
    const side = sideRaw === 2 ? 'SELL' : 'BUY';
    const sid = typeof td.symbolId === 'number' ? td.symbolId : 0;
    const sl = typeof p.stopLoss === 'number'
      ? p.stopLoss
      : (typeof td.stopLoss === 'number' ? td.stopLoss : null);
    const tp = typeof p.takeProfit === 'number'
      ? p.takeProfit
      : (typeof td.takeProfit === 'number' ? td.takeProfit : null);

    map.set(pid, {
      symbolName: symbolMap[sid] || ('ID:' + String(sid)),
      side,
      volume: volLots,
      sl,
      tp,
    });
  }
  return map;
}

/** Format a price (trim trailing zeros, up to 5 decimals). */
function fmtPrice(v: number | null): string {
  if (v === null || v === undefined || Number.isNaN(v)) return '—';
  return v.toFixed(5).replace(/\.?0+$/, '');
}

/** Build a stable idempotency key so the same event collapses cross-tab. */
function buildDedupKey(
  accountId: number,
  positionId: number,
  event: OrderEventType,
  signature?: string,
): string {
  const base = `order:${accountId}:${positionId}:${event.toLowerCase()}`;
  return signature ? `${base}:${signature}` : base;
}

/** Build the Telegram message body for an order change. */
function buildMessage(
  event: OrderEventType,
  accountLabel: string,
  state: OrderState,
  prev?: OrderState,
): string {
  const lines: string[] = [];
  lines.push('📊 <b>cTrader Order Alert</b>');

  if (event === 'OPEN') {
    lines.push(`🟢 OPEN — ${state.side} <b>${state.symbolName}</b> · ${state.volume.toFixed(2)} lot`);
  } else if (event === 'CLOSE') {
    lines.push(`🔴 CLOSE — ${state.side} <b>${state.symbolName}</b> · ${state.volume.toFixed(2)} lot`);
  } else {
    lines.push(`🟡 MODIFY — ${state.side} <b>${state.symbolName}</b>`);
  }

  lines.push(`Account: ${accountLabel}`);

  if (event === 'MODIFY' && prev) {
    lines.push(`SL: ${fmtPrice(prev.sl)} → ${fmtPrice(state.sl)}`);
    lines.push(`TP: ${fmtPrice(prev.tp)} → ${fmtPrice(state.tp)}`);
    if (prev.volume !== state.volume) {
      lines.push(`Volume: ${prev.volume.toFixed(2)} → ${state.volume.toFixed(2)} lot`);
    }
  } else if (event === 'OPEN') {
    lines.push(`SL: ${fmtPrice(state.sl)} · TP: ${fmtPrice(state.tp)}`);
  }

  return lines.join('\n');
}

/** Fire-and-forget Telegram alert for a detected order change. */
function fireAlert(
  accountId: number,
  positionId: number,
  event: OrderEventType,
  state: OrderState,
  prev?: OrderState,
): void {
  const label = accountLabels.get(accountKey(accountId)) || String(accountId);
  const message = buildMessage(event, label, state, prev);

  const signature =
    event === 'MODIFY'
      ? `${state.sl}|${state.tp}|${state.volume.toFixed(6)}`
      : undefined;

  sendTelegram(message, {
    category: TELEGRAM_CATEGORIES.ORDER_MODIFY,
    dedupKey: buildDedupKey(accountId, positionId, event, signature),
  }).then((result) => {
    if (result.success) {
      console.log(
        `[OrderAlert] ✅ ${event} ${state.symbolName} (${accountId}#${positionId}) sent`
      );
    } else {
      console.warn(
        `[OrderAlert] ⚠️ ${event} ${state.symbolName} (${accountId}#${positionId}) FAILED: ${result.error}`
      );
    }
  });
}

// ─── Snapshot diff ──────────────────────────────────────────

function handlePositionsEvent(
  accountId: number,
  reconcileRes: Record<string, unknown>,
  symbolRes: Record<string, unknown> | null,
): void {
  const key = accountKey(accountId);
  const next = parseOrderStates(reconcileRes, symbolRes);

  // Prime on first snapshot: record baseline WITHOUT firing (startup backlog).
  if (!primedByAccount.has(key)) {
    orderStatesByAccount.set(key, next);
    primedByAccount.add(key);
    console.log(
      `[OrderAlert] 🛡️ Primed ${accountId} — ${next.size} open position(s) recorded as baseline`
    );
    return;
  }

  const prev = orderStatesByAccount.get(key) || new Map<number, OrderState>();

  // OPEN: position appears in next but not prev.
  for (const [pid, state] of next) {
    if (!prev.has(pid)) {
      fireAlert(accountId, pid, 'OPEN', state);
    }
  }

  // CLOSE: position exists in prev but not next.
  for (const [pid, state] of prev) {
    if (!next.has(pid)) {
      fireAlert(accountId, pid, 'CLOSE', state);
    }
  }

  // MODIFY: position exists in both but sl/tp/volume changed.
  for (const [pid, state] of next) {
    const p = prev.get(pid);
    if (!p) continue;
    if (p.sl !== state.sl || p.tp !== state.tp || p.volume !== state.volume) {
      fireAlert(accountId, pid, 'MODIFY', state, p);
    }
  }

  orderStatesByAccount.set(key, next);
}

// ─── Public API ─────────────────────────────────────────────

/**
 * Start watching order changes for the given cTrader accounts.
 * Idempotent — accounts already watched are skipped; new accounts are added.
 */
export function startOrderAlertWatcher(accounts: OrderAlertAccount[]): void {
  for (const acc of accounts) {
    const key = accountKey(acc.accountId);
    accountLabels.set(key, acc.accountName || String(acc.accountId));

    if (unsubscribers.has(key)) continue;

    const unsub = subscribeSharedStream(
      {
        id: `order-alert__${acc.accountId}`,
        symbolIds: [],
        periods: [],
        symbolDigits: {},
        onEvent: (evt: StreamEvent) => {
          if (evt.type === 'positions' && evt.accountId !== undefined) {
            handlePositionsEvent(
              evt.accountId,
              (evt.reconcileRes as Record<string, unknown>) || {},
              (evt.symbolRes as Record<string, unknown> | null) || null,
            );
          }
        },
      },
      acc.accountId,
    );

    unsubscribers.set(key, unsub);
    console.log(`[OrderAlert] ➕ Watching cTrader account ${acc.accountId} (${acc.accountName})`);
  }
}

/**
 * Stop watching and release all subscriptions + state.
 * Called on logout.
 */
export function stopOrderAlertWatcher(): void {
  for (const [, unsub] of unsubscribers) {
    try { unsub(); } catch { /* defensive */ }
  }
  unsubscribers.clear();
  orderStatesByAccount.clear();
  accountLabels.clear();
  primedByAccount.clear();
  console.log('[OrderAlert] ✗ Stopped watching all accounts');
}

/** Whether the watcher is currently watching at least one account. */
export function isOrderAlertWatching(): boolean {
  return unsubscribers.size > 0;
}